import { useEffect, useState } from "react";
import { Button, Select } from "risa-oasis-ui_v2";
import CheckboxList from "../../../../../components/checkboxList/checkboxList";
import SideModal from "../../../../../components/modals/filterModal/filterModal";
import { useHealthCareFacilities } from "../../../../../hooks/useHealthCareFacilities";
import { OrderAssignmentConfiguration } from "../../../../../../shared/api/services/orderAssignmentConfigService";
import { CrossIcon } from "../../../../../../shared/svg/cross-icon";
import {
  capitalizeWordsSeperatedByUnderScore,
  substringMatch,
} from "../../../../../utils/stringModifications";
import { useAuthStatusOptions } from "../hooks/useAuthStatusOptions";
import { useConfigurationSelections } from "../hooks/useConfigurationSelections";
import { usePayerPatternOptions } from "../hooks/usePayerPatternOptions";
import {
  areAllSelected,
  comparePayerPatterns,
  convertToAnyIfAllSelected,
  handleOrganizationChange,
} from "../utils/orderAssignmentHelpers";
import CollapsibleSection from "./CollapsibleSection";
import "./EditConfigurationModal.scss";

interface EditConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: OrderAssignmentConfiguration) => void;
  config: OrderAssignmentConfiguration | null;
  facilityId: string;
  onOrgChange?: () => void; // Callback to refresh configurations after org change
}

const EditConfigurationModal = ({
  isOpen,
  onClose,
  onSave,
  config,
  facilityId,
  onOrgChange,
}: EditConfigurationModalProps) => {
  const [expandedSection, setExpandedSection] = useState<
    "auth" | "payer" | "org" | null
  >(null);
  const [payerSearchQuery, setPayerSearchQuery] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [isChangingOrg, setIsChangingOrg] = useState(false);

  const { healthCareFacilityOptions } = useHealthCareFacilities();

  const { payerPatternOptions, isLoading: payerLoading } =
    usePayerPatternOptions(facilityId, isOpen);
  const { authStatusOptions, isLoading: authStatusLoading } =
    useAuthStatusOptions(facilityId, isOpen);

  const {
    selectedAuthStatuses,
    selectedPayerPatterns,
    handleAuthStatusToggle,
    handlePayerPatternToggle,
    handleSelectAllAuthStatuses,
    handleClearAllAuthStatuses,
    handleSelectAllPayerPatterns,
    handleClearAllPayerPatterns,
    resetSelections,
  } = useConfigurationSelections(
    config,
    isOpen,
    payerPatternOptions,
    authStatusOptions,
  );

  // Reset search query and selected org when modal opens or config changes
  useEffect(() => {
    if (isOpen) {
      setPayerSearchQuery("");
      setSelectedOrgId("");
    }
  }, [isOpen, config]);

  const handleSave = () => {
    if (!config) return;

    const authStatusToSave = convertToAnyIfAllSelected(
      selectedAuthStatuses,
      authStatusOptions,
      (selected, option) => selected === option.id,
    );

    const payerPatternsToSave = convertToAnyIfAllSelected(
      selectedPayerPatterns,
      payerPatternOptions,
      comparePayerPatterns,
    );

    const updatedConfig: OrderAssignmentConfiguration = {
      ...config,
      auth_status: authStatusToSave,
      payer_patterns: payerPatternsToSave,
    };

    onSave(updatedConfig);
  };

  const handleCancel = () => {
    resetSelections();
    setPayerSearchQuery("");
    onClose();
  };

  // Accordion behavior: only one section open at a time
  const handleSectionToggle = (section: "auth" | "payer" | "org") => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  /**
   * Handle organization change - updates facility_id and resets all values to defaults
   */
  const handleOrgChange = async () => {
    setIsChangingOrg(true);
    try {
      await handleOrganizationChange(
        config,
        selectedOrgId,
        onOrgChange,
        handleCancel,
      );
    } catch (error) {
      // Error is already handled in the utility function
    } finally {
      setIsChangingOrg(false);
    }
  };

  const filteredPayerPatternOptions = payerPatternOptions.filter((pattern) =>
    substringMatch(pattern, payerSearchQuery),
  );

  // Check if all items are selected for "Select All" checkbox
  const allAuthStatusesSelected = areAllSelected(
    selectedAuthStatuses,
    authStatusOptions,
    (selected, option) => selected === option.id,
  );
  // For "Select All" checkbox UI: check if all filtered options are selected
  const allFilteredPayerPatternsSelected = areAllSelected(
    selectedPayerPatterns,
    filteredPayerPatternOptions,
    comparePayerPatterns,
  );

  if (!config) return null;

  return (
    <SideModal
      isOpen={isOpen}
      onClose={handleCancel}
      id="edit-configuration-modal"
    >
      <div className="flex h-full flex-col bg-white">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#e5e7eb] px-6 py-5">
          <h2 className="m-0 font-['Lato'] text-lg font-bold leading-[1.625rem]">
            {config.provider_name || "Edit Configuration"}
          </h2>
          <button
            className="flex cursor-pointer items-center justify-center border-none bg-transparent p-1 hover:opacity-70"
            onClick={handleCancel}
          >
            <CrossIcon fillColor="#374151" />
          </button>
        </div>

        {/* Content */}
        <div className="edit-config-modal__content flex flex-1 flex-col px-2 py-4">
          {payerLoading || authStatusLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="font-['Lato'] text-sm leading-[1.375rem] text-[#6B7280]">
                Loading options...
              </span>
            </div>
          ) : (
            <div className="edit-config-modal__sections flex flex-1 flex-col">
              {/* Auth Status Section */}
              <CollapsibleSection
                title="Auth Status"
                isExpanded={expandedSection === "auth"}
                onToggle={() => handleSectionToggle("auth")}
              >
                {authStatusOptions.length > 0 && (
                  <div className="border-b border-[#e5e7eb] px-4 py-2">
                    <div className="flex items-center justify-end gap-3">
                      <div
                        className="text-small font-semibold text-tertiaryBlue-4 hover:cursor-pointer hover:shadow-sm"
                        onClick={() =>
                          handleSelectAllAuthStatuses(
                            authStatusOptions.map((option) => option.id),
                          )
                        }
                      >
                        Select All
                      </div>
                      <div
                        className="text-small font-semibold text-tertiaryRed-4 hover:cursor-pointer hover:shadow-sm"
                        onClick={handleClearAllAuthStatuses}
                      >
                        Clear Selection
                      </div>
                    </div>
                  </div>
                )}
                <CheckboxList
                  items={authStatusOptions}
                  selectedItems={selectedAuthStatuses}
                  onToggle={(option) => handleAuthStatusToggle(option.id)}
                  getLabel={(option) => option.text}
                  getKey={(option) => option.id}
                  emptyMessage="No auth status options available"
                  idPrefix="auth-status"
                  disabled={allAuthStatusesSelected}
                />
              </CollapsibleSection>

              {/* Payer Section */}
              <CollapsibleSection
                title="Payer"
                isExpanded={expandedSection === "payer"}
                onToggle={() => handleSectionToggle("payer")}
              >
                {payerPatternOptions.length > 0 && (
                  <>
                    {/* Search Input with Select All and Clear Selection Buttons */}
                    <div className="border-b border-[#e5e7eb] px-4 py-2">
                      <div className="mb-2 flex items-center justify-end gap-3">
                        <div
                          className="text-small font-semibold text-tertiaryBlue-4 hover:cursor-pointer hover:shadow-sm"
                          onClick={() =>
                            handleSelectAllPayerPatterns(
                              filteredPayerPatternOptions,
                            )
                          }
                        >
                          Select All
                        </div>
                        <div
                          className="text-small font-semibold text-tertiaryRed-4 hover:cursor-pointer hover:shadow-sm"
                          onClick={handleClearAllPayerPatterns}
                        >
                          Clear Selection
                        </div>
                      </div>
                      <input
                        type="text"
                        placeholder="Search payers..."
                        value={payerSearchQuery}
                        onChange={(e) => setPayerSearchQuery(e.target.value)}
                        className="w-full rounded border border-[#d1d5db] px-3 py-2 text-sm leading-[1.375rem] text-[#374151] placeholder:text-[#9ca3af] focus:border-black focus:outline-none"
                      />
                    </div>
                  </>
                )}
                <CheckboxList
                  items={filteredPayerPatternOptions}
                  selectedItems={selectedPayerPatterns}
                  onToggle={(pattern) => handlePayerPatternToggle(pattern)}
                  getLabel={(pattern) =>
                    capitalizeWordsSeperatedByUnderScore(pattern)
                  }
                  getKey={(pattern) => pattern}
                  emptyMessage={
                    payerSearchQuery.trim()
                      ? "No payers match your search"
                      : "No payer patterns available"
                  }
                  idPrefix="payer-pattern"
                  disabled={allFilteredPayerPatternsSelected}
                  isItemSelected={(pattern, selected) => {
                    // Case-insensitive comparison to handle format differences
                    return (selected as string[]).some((selectedPattern) =>
                      comparePayerPatterns(selectedPattern, pattern),
                    );
                  }}
                />
              </CollapsibleSection>

              {/* Change Org Section */}
              <CollapsibleSection
                title="Change Organization"
                isExpanded={expandedSection === "org"}
                onToggle={() => handleSectionToggle("org")}
              >
                <div className="px-4 py-4">
                  <div className="mb-4">
                    <label className="mb-2 block text-sm font-medium text-[#374151]">
                      Select Organization
                    </label>
                    <Select
                      id="organization-select"
                      label=""
                      options={healthCareFacilityOptions.map((facility) => ({
                        label: facility.label,
                        value: facility.value,
                      }))}
                      onOptionChange={(option: { value: string }) => {
                        setSelectedOrgId(option.value);
                      }}
                      placeholder="Select an organization..."
                      defaultValue={selectedOrgId || ""}
                    />
                  </div>
                  <div className="mb-4 flex items-start gap-2 rounded-md border border-[#e5e7eb] bg-[#f9fafb] p-3">
                    <svg
                      className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#6B7280]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-xs leading-5 text-[#6B7280]">
                      <span className="font-semibold">Note:</span> Changing the
                      organization will reset all configuration settings to
                      their default value.
                    </p>
                  </div>
                  <Button
                    buttonType="primary"
                    size="medium"
                    onClick={handleOrgChange}
                    disabled={!selectedOrgId || isChangingOrg}
                  >
                    {isChangingOrg ? "Changing..." : "Confirm Change"}
                  </Button>
                </div>
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="edit-config-modal__footer mt-auto flex justify-center gap-3 border-t border-[#e5e7eb] bg-white px-6 py-4">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={handleCancel}
            disabled={false}
          >
            Cancel
          </Button>
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            disabled={false}
          >
            Save
          </Button>
        </div>
      </div>
    </SideModal>
  );
};

export default EditConfigurationModal;
